import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { formatTel } from '@/components/shared/utility/format';
import { googleMapURL } from '@/components/shared/utility/location';
import { styled } from '@/stitches.config';
import { IDealer } from '@/types/dealer/dealer';

const StyledHoverEffectDiv = styled('div', {
  display: 'block',
  position: 'absolute',
  left: '-1px',
  top: '-1px',
  width: 'calc(100% + 2px)',
  height: 'calc(100% + 2px)',
  bc: '$gray200',
  overflow: 'hidden',
  pe: 'none',
  clipPath: 'circle(0 at 50% 50%)',
  '--transition-duration': '0.4s',
});

export interface DealerResultDataListItemProps {
  item: IDealer;
  onOpenDrawer: (uid: string) => void;
  vehicleType: string;
}

export const DealerResultDataListItem: React.FunctionComponent<
  DealerResultDataListItemProps
> = (props: DealerResultDataListItemProps) => {
  return (
    <Box
      id={`dealer_${props.item.uid}`}
      test={{
        dataTest: 'dealer_result_item',
      }}
      position="relative"
      paddingX={{
        '@initial': '6',
        '@sm': '7',
      }}
      paddingY="3"
      overflow="hidden"
      css={{
        '&:not(:last-child)': {
          borderBottom: '1px solid $gray300',
        },
        '@hover': {
          '&:hover': {
            [`& ${StyledHoverEffectDiv}`]: {
              '--transition-duration': '0.5s',
              clipPath: 'circle(70.71068% at 50% 50%)',
              transitionProperty: 'clip-path',
              transitionDelay: 'var(--transition-delay)',
              transitionDuration: 'var(--transition-duration)',
            },
          },
        },
      }}
    >
      <StyledHoverEffectDiv />
      <Flex alignItems="center" justifyContent="between" gap="6">
        <Text as="p" css={{ minHeight: 48, gridColumn: '1 / span 3' }}>
          {props.item.mirai?.name_th}
        </Text>
        <Box
          position="absolute"
          cursor="pointer"
          inset="0"
          onClick={() => props.onOpenDrawer(props.item.uid)}
        />
        <Flex
          wrap="nowrap"
          alignItems="center"
          justifyContent="end"
          gap="3"
          css={{
            flex: '0 0 144px',
            maxWidth: '144px',
          }}
        >
          {props.vehicleType === 'cv' && !!props.item.sale?.fb_sale_cv && (
            <ButtonIcon
              href={`https://m.me/${props.item.sale.fb_sale_cv}?ref=website_dealer`}
              target="_blank"
              test={{
                dataTest: 'button_messenger',
                dataTestValue: 'button_messenger_cv',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `button_messenger`,
                dataTrackUrl: `https://m.me/${props.item.sale.fb_sale_cv}?ref=website_dealer`,
                dataTrackValue: `${props.item.sale.fb_sale_cv}`,
              }}
              icon="messenger"
              size="md"
              variant="button-icon-color-gray"
              border
            />
          )}
          {props.vehicleType === 'lcv' && !!props.item.sale?.fb_sale_lcv && (
            <ButtonIcon
              href={`https://m.me/${props.item.sale.fb_sale_lcv}?ref=website_dealer`}
              target="_blank"
              test={{
                dataTest: 'button_messenger',
                dataTestValue: 'button_messenger_lcv',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `button_messenger`,
                dataTrackUrl: `https://m.me/${props.item.sale.fb_sale_lcv}?ref=website_dealer`,
                dataTrackValue: `${props.item.sale?.fb_sale_lcv}`,
              }}
              icon="messenger"
              size="md"
              variant="button-icon-color-gray"
              border
            />
          )}
          {props.item.sale?.showroom_tel &&
          props.item.sale.showroom_tel.length > 0 ? (
            <ButtonIcon
              href={
                props.item.sale.showroom_tel[0].tel
                  ? 'tel:' +
                    formatTel(props.item.sale.showroom_tel[0].tel, true)
                  : ''
              }
              test={{
                dataTest: 'button_tel',
              }}
              tracking={{
                dataTrack: 'contact-dealer',
                dataTrackText: `button_tell_sale`,
                dataTrackUrl: `tel:${props.item.sale.showroom_tel[0].tel}`,
                dataTrackValue: `${props.item.sale.showroom_tel[0].tel}`,
              }}
              icon="local-phone"
              size="md"
              variant="button-icon-color-gray"
              border
            />
          ) : (
            props.item.aftersale?.service_center_tel &&
            props.item.aftersale.service_center_tel.length > 0 && (
              <ButtonIcon
                href={
                  props.item.aftersale.service_center_tel[0].tel
                    ? 'tel:' +
                      formatTel(
                        props.item.aftersale.service_center_tel[0].tel,
                        true
                      )
                    : ''
                }
                test={{
                  dataTest: 'button_tel',
                }}
                tracking={{
                  dataTrack: 'contact-dealer',
                  dataTrackText: `button_tell_sale`,
                  dataTrackUrl: `tel:${props.item.aftersale.service_center_tel[0].tel}`,
                  dataTrackValue: `${props.item.aftersale.service_center_tel[0].tel}`,
                }}
                icon="local-phone"
                size="md"
                variant="button-icon-color-gray"
                border
              />
            )
          )}
          <ButtonIcon
            href={googleMapURL({
              lat: props.item.sale?.lat ?? '',
              lng: props.item.sale?.lon ?? '',
              name: props.item.mirai?.name_th,
              url: props.item.sale?.google_map_url,
            })}
            target="_blank"
            test={{
              dataTest: 'button_googlemapMarker',
            }}
            tracking={{
              dataTrack: 'contact-dealer',
              dataTrackText: `button_googlemapMarker`,
              dataTrackUrl: `${googleMapURL({
                lat: props.item.sale?.lat ?? '',
                lng: props.item.sale?.lon ?? '',
                name: props.item.mirai?.name_th,
                url: props.item.sale?.google_map_url,
              })}`,
              dataTrackValue: `${props.item.mirai?.name_th} lat=${props.item.sale?.lat} lon=${props.item.sale?.lon}`,
            }}
            icon="place"
            size="md"
            variant="button-icon-color-gray"
            border
          />
        </Flex>
      </Flex>
    </Box>
  );
};

DealerResultDataListItem.displayName = 'SectionDealerResultDataListItem';
